// Skill tree rules: point budget, tier gating, and the stat source.
import { D, type Decimal } from '../core/decimal';
import { NODE_BY_ID, SKILL_POINTS, TIER_REQUIREMENT, TREE, type BranchId, type TreeNode } from '../data/skillTree';
import type { GameState } from './state';
import type { AddStat, ModifierSource } from './stats';

export function totalSkillPoints(state: GameState): number {
  const fromStage = Math.floor(state.bestStage / SKILL_POINTS.stagesPerPoint);
  const fromAscensions = Math.min(SKILL_POINTS.ascensionCap, state.ascensions * SKILL_POINTS.perAscension);
  return fromStage + fromAscensions;
}

export function spentInBranch(state: GameState, branch: BranchId): number {
  let n = 0;
  for (const node of TREE) if (node.branch === branch) n += (state.skillTree[node.id] ?? 0) * node.tier;
  return n;
}

export function spentTotal(state: GameState): number {
  let n = 0;
  for (const node of TREE) n += (state.skillTree[node.id] ?? 0) * node.tier;
  return n;
}

export function availableSkillPoints(state: GameState): number {
  return totalSkillPoints(state) - spentTotal(state);
}

export function tierOpen(state: GameState, node: TreeNode): boolean {
  return spentInBranch(state, node.branch) >= TIER_REQUIREMENT[node.tier];
}

export type NodeBlock = 'max' | 'tier' | 'points' | null;

export function nodeBlocked(state: GameState, id: string): NodeBlock {
  const node = NODE_BY_ID.get(id)!;
  if ((state.skillTree[id] ?? 0) >= node.maxLevel) return 'max';
  if (!tierOpen(state, node)) return 'tier';
  if (availableSkillPoints(state) < node.tier) return 'points';
  return null;
}

export function nodeValue(node: TreeNode, level: number): Decimal {
  const e = node.effect;
  if (e.kind === 'mult') return D(1 + e.perLevel * level);
  if (e.kind === 'div') return D(1 / (1 + e.perLevel * level));
  return D(e.perLevel * level);
}

const ADD_FORMAT: Partial<Record<AddStat, (v: number) => string>> = {
  critChance: (v) => `+${(v * 100).toFixed(0)}%`,
  tapDpsShare: (v) => `+${(v * 100).toFixed(1)}%`,
  manaRegenPct: (v) => `+${Math.round(v * 100)}%`,
  skillDurationPct: (v) => `+${Math.round(v * 100)}%`,
  offlineEfficiency: (v) => `+${Math.round(v * 100)}%`,
  bossTimeSec: (v) => `+${v}s`,
  maxMana: (v) => `+${v}`,
  frenzyRate: (v) => `+${v}`,
  warcryBonus: (v) => `+x${v.toFixed(1)}`,
  fireswordBonus: (v) => `+x${v.toFixed(1)}`,
};

export function describeNode(node: TreeNode, level: number): string {
  const v = nodeValue(node, level).toNumber();
  const e = node.effect;
  const text = e.kind === 'add' ? (ADD_FORMAT[e.stat] ?? ((x: number) => `+${x}`))(v) : `x${v.toFixed(2)}`;
  return node.desc.replace('{v}', text);
}

export function skillTreeSource(): ModifierSource {
  return {
    id: 'skillTree',
    apply(state, m) {
      for (const node of TREE) {
        const level = state.skillTree[node.id] ?? 0;
        if (level <= 0) continue;
        const e = node.effect;
        if (e.kind === 'add') m.add(e.stat, nodeValue(node, level).toNumber());
        else m.mult(e.stat, nodeValue(node, level));
      }
    },
  };
}
